import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  IconButton, 
  Tooltip, 
  useTheme,
  Chip,
  Divider,
  Paper,
  Button,
  Alert,
  Fade,
  alpha
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import FileViewerStyles from '../../../styles/purchaseInvoices/invoiceManagement/FileViewerStyles';
import useFileViewer from '../../../hooks/purchaseInvoices/invoiceManagement/useFileViewer';
import { isPDFFile, getImageSrc, getPDFSrc } from '../../../Utils/purchaseInvoices/invoiceManagement/fileUtils';

const FileViewer = ({ 
  fileAsBase64, 
  fileName, 
  isLoadingFile,
  height = '60vh',
  currentFile = null,
  allFiles = [],
  currentFileIndex = 0,
  totalFiles = 0,
  hasNextFile = false,
  hasPreviousFile = false,
  onNextFile = null,
  onPreviousFile = null,
  onGoToFile = null,
  isLoadingFiles = false
}) => {
  const theme = useTheme();
  const [displayError, setDisplayError] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  
  const {
    containerRef,
    zoomLevel,
    rotation,
    numPages,
    containerWidth,
    handleZoomIn,
    handleZoomOut,
    handleRotateLeft,
    handleRotateRight,
    handleFullscreen,
    handleDownload: downloadFile,
    onDocumentLoadSuccess
  } = useFileViewer();

  const handleScroll = () => {
    setIsScrolling(true);
    
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      setIsScrolling(false);
    }, 2000);
    
    setScrollTimeout(newTimeout);
  };

  useEffect(() => {
    const viewerContainer = containerRef.current;
    if (viewerContainer) {
      viewerContainer.addEventListener('scroll', handleScroll);
      return () => {
        viewerContainer.removeEventListener('scroll', handleScroll);
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
      };
    }
  }, [containerRef.current, scrollTimeout]);

  let displayFile, isGalleryMode, displayFileName, displayFileContent;
  
  if (currentFile && allFiles.length > 0) {
    isGalleryMode = true;
    displayFile = currentFile;
    displayFileName = currentFile.displayName || currentFile.name || 'קובץ';
    displayFileContent = currentFile.fileContent;
  } else {
    isGalleryMode = false;
    displayFile = { fileContent: fileAsBase64, name: fileName };
    displayFileName = fileName || 'קובץ חשבונית';
    displayFileContent = fileAsBase64;
  }

  const isLoading = isLoadingFiles || isLoadingFile || 
                   (isGalleryMode && displayFile && !displayFile.fileContent && displayFile.type === 'attachment');

  const isPDF = isPDFFile(displayFileContent, displayFileName);
  
  const cannotDisplayFile = currentFile?.type === 'attachment' && 
                            (!displayFileContent || displayError || pdfError);

  const handleDownload = () => {
    const fileNameToUse = displayFile.name || fileName || 'invoice_file';
    downloadFile(displayFileContent, isPDF, () => getImageSrc(displayFileContent), fileNameToUse);
  };

  const handleDownloadAttachment = async () => {
    if (!currentFile || currentFile.type !== 'attachment') return;
    
    try {
      if (currentFile.downloadUrl) {
        const link = document.createElement('a');
        link.href = currentFile.downloadUrl;
        link.download = currentFile.originalFileName || currentFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (displayFileContent) {
        handleDownload();
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const getFileTypeConfig = (fileType) => {
    const configs = {
      'original': {
        color: '#1976d2',
        bgColor: '#e8f4fd',
        lightColor: '#bbdefb',
        label: 'מקורית',
        icon: DescriptionIcon
      },
      'signed': {
        color: '#2e7d32',
        bgColor: '#e8f8e9',
        lightColor: '#c8e6c9',
        label: 'חתומה',
        icon: CheckCircleIcon
      },
      'attachment': {
        color: '#7b1fa2',
        bgColor: '#f3e5f5',
        lightColor: '#e1bee7',
        label: 'צרופה',
        icon: AttachFileIcon
      }
    };
    return configs[fileType] || configs['attachment'];
  };

  const getFileIcon = (fileName, fileType) => {
    if (fileType === 'attachment') {
      const extension = fileName?.split('.').pop()?.toLowerCase();
      if (extension === 'pdf') return PictureAsPdfIcon;
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return ImageIcon;
      return DescriptionIcon;
    }
    return getFileTypeConfig(fileType).icon;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const AttachmentNotViewable = () => {
    const extension = displayFileName?.split('.').pop()?.toLowerCase() || '';
    const IconComponent = getFileIcon(displayFileName, 'attachment');
    
    return (
      <Box sx={FileViewerStyles.attachmentNotViewable.container}>
        <Box sx={FileViewerStyles.attachmentNotViewable.iconContainer(theme)}>
          <IconComponent sx={{ fontSize: 40, color: theme.palette.secondary.main }} />
        </Box>

        <Typography variant="h6" color="text.primary" fontWeight="bold" sx={FileViewerStyles.attachmentNotViewable.title}>
          {displayFileName}
        </Typography>

        <Alert 
          severity="info" 
          icon={<VisibilityOffIcon />}
          sx={FileViewerStyles.attachmentNotViewable.alert}
        >
          <Typography variant="body2">
            הקובץ זמין להורדה אך לא ניתן לתצוגה במערכת
          </Typography>
        </Alert>

        <Box sx={FileViewerStyles.attachmentNotViewable.metaContainer}>
          <Chip 
            label={getFileTypeConfig('attachment').label}
            color="secondary"
            size="small"
          />
          
          {currentFile?.size && (
            <Chip 
              label={formatFileSize(currentFile.size)}
              variant="outlined"
              size="small"
            />
          )}
          
          {extension && (
            <Chip 
              label={extension.toUpperCase()}
              variant="outlined"
              size="small"
              color="primary"
            />
          )}
        </Box>

        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleDownloadAttachment}
          size="large"
          sx={FileViewerStyles.attachmentNotViewable.downloadButton}
        >
          הורד קובץ
        </Button>

        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={FileViewerStyles.attachmentNotViewable.description}
        >
          הקובץ יורד למחשב שלך ויתן לפתוח אותו ביישום המתאים
        </Typography>
      </Box>
    );
  };

  const PDFError = () => (
    <Box sx={FileViewerStyles.pdfError.container}>
      <WarningAmberIcon sx={FileViewerStyles.pdfError.icon} />
      <Typography variant="h6" sx={FileViewerStyles.pdfError.title}>
        שגיאה בטעינת קובץ PDF
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={FileViewerStyles.pdfError.description}>
        הקובץ עשוי להיות פגום או בפורמט לא נתמך
      </Typography>
      <Button 
        variant="contained" 
        startIcon={<DownloadIcon />}
        onClick={handleDownloadAttachment}
      >
        הורד קובץ
      </Button>
    </Box>
  );

  const NavigationDots = () => (
    <Fade in={!isScrolling} timeout={300}>
      <Paper sx={FileViewerStyles.navigationDots(theme)}>
        {allFiles.map((file, index) => {
          const isActive = index === currentFileIndex;
          const config = getFileTypeConfig(file.type);
          
          return (
            <Tooltip 
              key={file.id}
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    חשבונית {config.label}
                  </Typography>
                  <Typography variant="caption">
                    {file.displayName || file.name}
                  </Typography>
                  {file.type === 'attachment' && file.size && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {formatFileSize(file.size)}
                    </Typography>
                  )}
                </Box>
              }
              placement="top"
            >
              <Box
                onClick={() => onGoToFile && onGoToFile(index)}
                sx={FileViewerStyles.navigationDot}
              >
                <FiberManualRecordIcon 
                  sx={{
                    fontSize: isActive ? 14 : 10,
                    color: config.color,
                    opacity: isActive ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                    filter: isActive ? `drop-shadow(0 0 3px ${config.color})` : 'none',
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.15)'
                    }
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Paper>
    </Fade>
  );

  const EnhancedFileTypeTabs = () => (
    <Box sx={FileViewerStyles.tabsContainer(theme)}>
      {allFiles.map((file, index) => {
        const isActive = index === currentFileIndex;
        const config = getFileTypeConfig(file.type);
        const IconComponent = getFileIcon(file.name, file.type);
        
        return (
          <Paper
            key={file.id}
            onClick={() => onGoToFile && onGoToFile(index)}
            elevation={0}
            sx={FileViewerStyles.tab(theme, isActive, config)}
          >
              <Box sx={FileViewerStyles.tabIcon(theme, isActive, config)}>
                <IconComponent sx={{ fontSize: 18 }} />
              </Box>
              
              <Box sx={FileViewerStyles.tabContent}>
                <Typography 
                  variant="body2" 
                  sx={FileViewerStyles.tabTitle(isActive, config)}
                  noWrap
                >
                  חשבונית {config.label}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {file.type === 'attachment' && file.size && (
                    <Chip
                      label={formatFileSize(file.size)}
                      size="small"
                      sx={FileViewerStyles.tabChip(config)}
                    />
                  )}
                  
                  {isActive && (
                    <Box sx={FileViewerStyles.activeIndicator(config)} />
                  )}
                </Box>
              </Box>
            </Paper>
        );
      })}
    </Box>
  );

  useEffect(() => {
    setDisplayError(false);
    setPdfError(false);
  }, [currentFileIndex, displayFileContent]);

  return (
    <Card sx={FileViewerStyles.mainContainer(theme)}>
      {isGalleryMode && allFiles.length > 0 && <EnhancedFileTypeTabs />}
      
      <Box sx={FileViewerStyles.toolbar(theme)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" sx={FileViewerStyles.toolbarTitle}>
            {isGalleryMode && currentFile?.type && (
              <Box sx={FileViewerStyles.toolbarIcon(theme, currentFile)}>
                {React.createElement(getFileIcon(currentFile.name, currentFile.type), {
                  sx: { fontSize: 14 }
                })}
              </Box>
            )}
            {displayFileName}
          </Typography>
          
          {isGalleryMode && totalFiles > 1 && (
            <Chip 
              label={`${currentFileIndex + 1} / ${totalFiles}`}
              size="small"
              variant="outlined"
              sx={FileViewerStyles.fileCounter}
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isGalleryMode && totalFiles > 1 && (
            <>
              <IconButton 
                onClick={onPreviousFile} 
                size="small"
                disabled={!hasPreviousFile}
                sx={FileViewerStyles.navButton(theme)}
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              <IconButton 
                onClick={onNextFile} 
                size="small"
                disabled={!hasNextFile}
                sx={FileViewerStyles.navButton(theme)}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            </>
          )}
          
          {!cannotDisplayFile && (
            <>
              <IconButton onClick={handleZoomIn} size="small" sx={FileViewerStyles.controlButton(theme)}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleZoomOut} size="small" sx={FileViewerStyles.controlButton(theme)}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleRotateLeft} size="small" sx={FileViewerStyles.controlButton(theme)}>
                <RotateLeftIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleRotateRight} size="small" sx={FileViewerStyles.controlButton(theme)}>
                <RotateRightIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleFullscreen} size="small" sx={FileViewerStyles.controlButton(theme)}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </>
          )}
          
          <IconButton 
            onClick={cannotDisplayFile ? handleDownloadAttachment : handleDownload} 
            size="small"
            sx={FileViewerStyles.downloadButton(theme)}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      <Box 
        ref={containerRef}
        sx={{ 
          ...FileViewerStyles.viewerContainer, 
          height,
          position: 'relative'
        }}
      >
        {isLoading ? (
          <Box sx={FileViewerStyles.loadingContainer}>
            <Box sx={FileViewerStyles.loadingSpinner}></Box>
            <Typography variant="h6" sx={FileViewerStyles.loadingText}>
              {isLoadingFiles ? 'טוען קבצי חשבונית...' : 'טוען קובץ...'}
            </Typography>
            {isGalleryMode && (
              <Typography variant="body2" color="text.secondary">
                מחפש קבצים נוספים...
              </Typography>
            )}
          </Box>
        ) : cannotDisplayFile ? (
          <AttachmentNotViewable />
        ) : displayFileContent ? (
          <Fade in timeout={400}>
            <Box 
              id="file-viewer-frame"
              sx={FileViewerStyles.fileViewerFrame}
            >
              {isPDF ? (
                <Document
                  file={getPDFSrc(displayFileContent)}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={() => setPdfError(true)}
                  loading={<Typography>טוען PDF...</Typography>}
                  error={<PDFError />}
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={containerWidth > 0 ? Math.min(containerWidth * zoomLevel, containerWidth * 3) : undefined}
                      rotate={rotation}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadError={() => setPdfError(true)}
                    />
                  ))}
                </Document>
              ) : (
                <img
                  src={getImageSrc(displayFileContent)}
                  alt="Invoice"
                  style={FileViewerStyles.imageStyle(zoomLevel, rotation)}
                  onError={() => setDisplayError(true)}
                />
              )}
            </Box>
          </Fade>
        ) : (
          <Box sx={FileViewerStyles.noFileContainer}>
            <AttachFileIcon sx={FileViewerStyles.noFileIcon} />
            <Typography variant="h6" sx={FileViewerStyles.noFileTitle}>
              לא נמצא קובץ חשבונית
            </Typography>
            {isGalleryMode && totalFiles > 1 && (
              <Typography variant="body2" color="text.secondary">
                השתמש בחצים או במקשי החצים במקלדת לניווט בין הקבצים
              </Typography>
            )}
          </Box>
        )}
        
        {isGalleryMode && totalFiles > 1 && <NavigationDots />}
      </Box>
    </Card>
  );
};

export default FileViewer;